import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiDollarSign, FiPlus, FiSearch, FiRefreshCw, FiDownload,
  FiTrash2, FiEdit2, FiCheckCircle, FiClock, FiCalendar,
  FiFileText, FiTag, FiTrendingUp, FiCheck, FiX, FiEye,
  FiFilter, FiAlertCircle, FiCreditCard, FiSmartphone
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { agencyExpenseAPI } from '../../services/api';
import ExpenseFormModal from './ExpenseFormModal';
import Modal from '../../components/shared/Modal';

// Configurations visuelles pour les statuts
const STATUS_CONFIG = {
  paid: { label: 'Payé', color: '#15803d', bg: '#dcfce7', icon: FiCheckCircle },
  pending: { label: 'En attente', color: '#b45309', bg: '#fef3c7', icon: FiClock },
  planned: { label: 'Planifié', color: '#4338ca', bg: '#e0e7ff', icon: FiCalendar },
};

// Configurations visuelles pour les modes de paiement
const METHOD_CONFIG = {
  cash: { label: 'Espèces / Caisse', icon: '💵' },
  orange_money: { label: 'Orange Money', icon: '📱' },
  moov_money: { label: 'Moov Money', icon: '📱' },
  wave: { label: 'Wave', icon: '🌊' },
  bank_transfer: { label: 'Virement bancaire', icon: '🏦' },
  check: { label: 'Chèque', icon: '📜' },
  card: { label: 'Carte bancaire', icon: '💳' },
  other: { label: 'Autre', icon: '🔹' },
};

const AgencyExpensesPage = () => {
  // États de données
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);

  // Filtres
  const [periodPreset, setPeriodPreset] = useState('this_month'); // 'this_month', 'last_month', 'this_year', 'all', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedMethod, setSelectedMethod] = useState('all');

  // Pagination
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 25,
    offset: 0,
    pages: 1,
    currentPage: 1,
  });

  // Modals
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Modal Justificatif
  const [receiptModal, setReceiptModal] = useState(null);

  // Modal Nouvelle Catégorie
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [newCatData, setNewCatData] = useState({ name: '', color: '#2563eb', description: '' });
  const [catSubmitting, setCatSubmitting] = useState(false);

  // Calcul des dates selon le filtre de période
  const getDateRange = useCallback(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();

    if (periodPreset === 'this_month') {
      const start = new Date(y, m, 1).toISOString().split('T')[0];
      const end = new Date(y, m + 1, 0).toISOString().split('T')[0];
      return { startDate: start, endDate: end };
    }
    if (periodPreset === 'last_month') {
      const start = new Date(y, m - 1, 1).toISOString().split('T')[0];
      const end = new Date(y, m, 0).toISOString().split('T')[0];
      return { startDate: start, endDate: end };
    }
    if (periodPreset === 'this_year') {
      const start = `${y}-01-01`;
      const end = `${y}-12-31`;
      return { startDate: start, endDate: end };
    }
    if (periodPreset === 'custom') {
      return {
        startDate: customStartDate || undefined,
        endDate: customEndDate || undefined,
      };
    }
    return {};
  }, [periodPreset, customStartDate, customEndDate]);

  // Chargement des catégories
  const fetchCategories = async () => {
    try {
      const res = await agencyExpenseAPI.getCategories();
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  // Chargement des statistiques
  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { startDate, endDate } = getDateRange();
      const res = await agencyExpenseAPI.getStats({ startDate, endDate });
      setStats(res.data);
    } catch (err) {
      console.error('Erreur chargement stats dépenses:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [getDateRange]);

  // Chargement de la liste des dépenses
  const fetchExpenses = useCallback(async (newOffset = 0) => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      const params = {
        limit: pagination.limit,
        offset: newOffset,
        category_code: selectedCategory !== 'all' ? selectedCategory : undefined,
        payment_status: selectedStatus !== 'all' ? selectedStatus : undefined,
        payment_method: selectedMethod !== 'all' ? selectedMethod : undefined,
        search: searchTerm.trim() || undefined,
        startDate,
        endDate,
      };

      const res = await agencyExpenseAPI.list(params);
      setExpenses(res.data?.expenses || []);
      if (res.data?.pagination) {
        setPagination((prev) => ({
          ...prev,
          ...res.data.pagination,
          offset: newOffset,
        }));
      }
    } catch (err) {
      console.error('Erreur chargement dépenses:', err);
      toast.error('Erreur lors du chargement des dépenses');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, selectedCategory, selectedStatus, selectedMethod, searchTerm, pagination.limit]);

  // Initialisation
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchStats();
    fetchExpenses(0);
  }, [fetchStats, fetchExpenses]);

  // Création / Édition Dépense
  const handleSaveExpense = async (formData) => {
    setFormSubmitting(true);
    try {
      if (editingExpense) {
        await agencyExpenseAPI.update(editingExpense.id, formData);
        toast.success('Dépense mise à jour avec succès');
      } else {
        await agencyExpenseAPI.create(formData);
        toast.success('Dépense enregistrée avec succès');
      }
      setFormModalOpen(false);
      setEditingExpense(null);
      fetchExpenses(pagination.offset);
      fetchStats();
    } catch (err) {
      console.error('Erreur enregistrement dépense:', err);
      toast.error(err.response?.data?.error || 'Erreur lors de l\'enregistrement de la dépense');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Suppression
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Confirmez-vous la suppression de la dépense "${title}" ?`)) return;
    try {
      await agencyExpenseAPI.delete(id);
      toast.success('Dépense supprimée');
      fetchExpenses(pagination.offset);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // Marquer comme Payé en un clic
  const handleQuickMarkPaid = async (expense) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const formData = new FormData();
      formData.append('payment_status', 'paid');
      formData.append('payment_date', today);

      await agencyExpenseAPI.update(expense.id, formData);
      toast.success(`Dépense "${expense.title}" marquée comme payée.`);
      fetchExpenses(pagination.offset);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la mise à jour du statut');
    }
  };

  // Création d'une catégorie personnalisée
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCatData.name.trim()) return;
    setCatSubmitting(true);
    try {
      await agencyExpenseAPI.createCategory(newCatData);
      toast.success(`Catégorie "${newCatData.name}" ajoutée !`);
      setCatModalOpen(false);
      setNewCatData({ name: '', color: '#2563eb', description: '' });
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la création de la catégorie');
    } finally {
      setCatSubmitting(false);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (expenses.length === 0) {
      toast.warning('Aucune dépense à exporter.');
      return;
    }

    const headers = ['Date Dépense', 'Réf Justificatif', 'Libellé', 'Catégorie', 'Bénéficiaire', 'Mode de Paiement', 'Montant (FCFA)', 'Statut', 'Date Paiement', 'Récurrente', 'Description'];
    const rows = expenses.map((e) => [
      e.expense_date ? String(e.expense_date).split('T')[0] : '',
      `"${(e.receipt_reference || '').replace(/"/g, '""')}"`,
      `"${(e.title || '').replace(/"/g, '""')}"`,
      `"${(e.category_name || e.category_code || '').replace(/"/g, '""')}"`,
      `"${(e.beneficiary || '').replace(/"/g, '""')}"`,
      METHOD_CONFIG[e.payment_method]?.label || e.payment_method || '',
      parseFloat(e.amount || 0),
      STATUS_CONFIG[e.payment_status]?.label || e.payment_status || '',
      e.payment_date ? String(e.payment_date).split('T')[0] : '',
      e.is_recurring ? 'Oui' : 'Non',
      `"${(e.description || '').replace(/"/g, '""')}"`,
    ]);

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Depenses_Agence_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Fichier CSV exporté avec succès');
  };

  // Formatage Montant
  const formatMoney = (val) => {
    const num = parseFloat(val) || 0;
    return `${num.toLocaleString('fr-FR')} FCFA`;
  };

  // Formatage Date
  const formatDate = (d) => {
    if (!d) return '-';
    try {
      const date = new Date(d);
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return String(d).split('T')[0];
    }
  };

  // Calcul du max pour le graphique mensuel
  const maxMonthlyAmount = useMemo(() => {
    if (!stats?.monthly_trend || stats.monthly_trend.length === 0) return 1;
    return Math.max(...stats.monthly_trend.map((m) => m.total_amount), 1);
  }, [stats]);

  return (
    <div className="page-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 16px' }}>
      {/* ── EN-TÊTE DE LA PAGE ─────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 20,
          background: '#ffffff',
          padding: '16px 20px',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px 0', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '6px 10px', borderRadius: 8, fontSize: 18 }}>
              💼
            </span>
            Gestion des Dépenses & Charges
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
            Suivi des coûts d'exploitation : loyer, électricité EDM, eau SOMAGEP, bons carburant, salaires et charges courantes.
          </p>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleExportCSV}
            title="Exporter les dépenses au format Excel/CSV"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
          >
            <FiDownload /> Exporter CSV
          </button>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setCatModalOpen(true)}
            title="Ajouter une nouvelle catégorie personnalisée"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
          >
            <FiTag /> + Catégorie
          </button>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setEditingExpense(null);
              setFormModalOpen(true);
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, padding: '9px 18px' }}
          >
            <FiPlus size={18} /> Nouvelle Dépense
          </button>
        </div>
      </div>

      {/* ── BARRE DE SÉLECTION DE PÉRIODE ──────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 20,
          background: '#f8fafc',
          padding: '10px 16px',
          borderRadius: 10,
          border: '1px solid #e2e8f0',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginRight: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            <FiCalendar /> Période :
          </span>
          {[
            { id: 'this_month', label: 'Ce mois-ci' },
            { id: 'last_month', label: 'Mois dernier' },
            { id: 'this_year', label: 'Cette année' },
            { id: 'all', label: 'Toutes les dates' },
            { id: 'custom', label: 'Personnalisée' },
          ].map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => setPeriodPreset(preset.id)}
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                border: periodPreset === preset.id ? '1px solid #2563eb' : '1px solid #cbd5e1',
                background: periodPreset === preset.id ? '#2563eb' : '#ffffff',
                color: periodPreset === preset.id ? '#ffffff' : '#334155',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {periodPreset === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="date"
              className="form-control form-control-sm"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              placeholder="Date début"
              style={{ width: 'auto', fontSize: 12 }}
            />
            <span style={{ color: '#64748b', fontSize: 12 }}>à</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              placeholder="Date fin"
              style={{ width: 'auto', fontSize: 12 }}
            />
          </div>
        )}
      </div>

      {/* ── CARTES STATISTIQUES (KPIs) ─────────────────────────────────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
          marginBottom: 24,
        }}
      >
        {/* Total des dépenses */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 12,
            padding: '18px 20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: '#eff6ff',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            <FiDollarSign />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Dépenses
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginTop: 2 }}>
              {loadingStats ? '...' : formatMoney(stats?.total_amount)}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              {stats?.total_count || 0} dépense{(stats?.total_count || 0) > 1 ? 's' : ''} au total
            </div>
          </div>
        </div>

        {/* Dépenses Réglées */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 12,
            padding: '18px 20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: '#f0fdf4',
              color: '#16a34a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            <FiCheckCircle />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Dépenses Réglées
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#15803d', lineHeight: 1.2, marginTop: 2 }}>
              {loadingStats ? '...' : formatMoney(stats?.paid_amount)}
            </div>
            <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, marginTop: 4 }}>
              {stats?.paid_count || 0} réglée{(stats?.paid_count || 0) > 1 ? 's' : ''} (
              {stats?.total_amount > 0 ? Math.round(((stats?.paid_amount || 0) / stats.total_amount) * 100) : 0}%)
            </div>
          </div>
        </div>

        {/* Dépenses En Attente */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 12,
            padding: '18px 20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: '#fffbeb',
              color: '#d97706',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            <FiClock />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              En Attente de Paiement
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#b45309', lineHeight: 1.2, marginTop: 2 }}>
              {loadingStats ? '...' : formatMoney(stats?.pending_amount)}
            </div>
            <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600, marginTop: 4 }}>
              {stats?.pending_count || 0} facture{(stats?.pending_count || 0) > 1 ? 's' : ''} en attente
            </div>
          </div>
        </div>

        {/* Top Catégorie */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 12,
            padding: '18px 20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: '#faf5ff',
              color: '#7c3aed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              flexShrink: 0,
            }}
          >
            <FiTrendingUp />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Poste Principal
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginTop: 2 }}>
              {loadingStats ? '...' : (stats?.top_category?.category_name || 'Aucune')}
            </div>
            <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600, marginTop: 4 }}>
              {stats?.top_category ? formatMoney(stats.top_category.total_amount) : '-'}
            </div>
          </div>
        </div>
      </div>

      {/* ── SECTION ANALYTIQUE (RÉPARTITION & HISTORIQUE 12 MOIS) ──────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: 16,
          marginBottom: 24,
        }}
      >
        {/* Répartition par Catégorie */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 12,
            padding: 20,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>📊 Répartition par Poste de Charge</span>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
              {stats?.category_breakdown?.length || 0} catégories
            </span>
          </h3>

          {loadingStats ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>Chargement de la répartition...</div>
          ) : !stats?.category_breakdown || stats.category_breakdown.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: 13 }}>
              Aucune dépense enregistrée sur cette période.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {stats.category_breakdown.slice(0, 6).map((cat, idx) => (
                <div key={idx}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, fontSize: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: cat.color || '#2563eb',
                          display: 'inline-block',
                        }}
                      />
                      <span style={{ fontWeight: 600, color: '#334155' }}>{cat.name}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>({cat.count})</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{formatMoney(cat.total_amount)}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', minWidth: 38, textAlign: 'right' }}>
                        {cat.percentage}%
                      </span>
                    </div>
                  </div>
                  {/* Barre de progression */}
                  <div style={{ width: '100%', height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${Math.min(parseFloat(cat.percentage) || 0, 100)}%`,
                        height: '100%',
                        background: cat.color || '#2563eb',
                        borderRadius: 3,
                        transition: 'width 0.5s ease-in-out',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Évolution Mensuelle des Dépenses (12 derniers mois) */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: 12,
            padding: 20,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 16px 0', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>📈 Tendance Mensuelle (12 Derniers Mois)</span>
            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Charges réelles</span>
          </h3>

          {loadingStats ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>Chargement du graphique...</div>
          ) : !stats?.monthly_trend || stats.monthly_trend.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: 13 }}>
              Aucune donnée historique disponible.
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: 160, gap: 8, paddingTop: 10 }}>
              {stats.monthly_trend.map((m, idx) => {
                const heightPct = Math.max(Math.round((m.total_amount / maxMonthlyAmount) * 100), 4);
                return (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                      justifyContent: 'flex-end',
                    }}
                    title={`${m.month_label} : ${formatMoney(m.total_amount)} (${m.count} dépenses)`}
                  >
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
                      {m.total_amount > 0 ? `${Math.round(m.total_amount / 1000)}k` : '0'}
                    </div>
                    <div
                      style={{
                        width: '100%',
                        maxWidth: 24,
                        height: `${heightPct}%`,
                        background: m.total_amount > 0 ? 'linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%)' : '#e2e8f0',
                        borderRadius: '4px 4px 0 0',
                        transition: 'height 0.4s ease',
                      }}
                    />
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 6, fontWeight: 600 }}>
                      {m.month_label ? m.month_label.split(' ')[0].substring(0, 3) : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── BARRE DE RECHERCHE ET FILTRES ──────────────────────────────── */}
      <div
        style={{
          background: '#ffffff',
          borderRadius: 12,
          padding: 16,
          border: '1px solid #e2e8f0',
          marginBottom: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            alignItems: 'center',
          }}
        >
          {/* Recherche */}
          <div style={{ position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              className="form-control"
              placeholder="Rechercher par titre, bénéficiaire, réf..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: 36, fontSize: 13 }}
            />
          </div>

          {/* Filtre Catégorie */}
          <div>
            <select
              className="form-control"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ fontSize: 13 }}
            >
              <option value="all">📂 Toutes les catégories</option>
              {categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre Statut */}
          <div>
            <select
              className="form-control"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{ fontSize: 13 }}
            >
              <option value="all">📌 Tous les statuts</option>
              <option value="paid">✅ Payé</option>
              <option value="pending">⏳ En attente</option>
              <option value="planned">📅 Planifié</option>
            </select>
          </div>

          {/* Filtre Mode de Paiement */}
          <div>
            <select
              className="form-control"
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              style={{ fontSize: 13 }}
            >
              <option value="all">💳 Tous les modes de paiement</option>
              <option value="cash">💵 Espèces</option>
              <option value="orange_money">📱 Orange Money</option>
              <option value="moov_money">📱 Moov Money</option>
              <option value="wave">🌊 Wave</option>
              <option value="bank_transfer">🏦 Virement bancaire</option>
              <option value="check">📜 Chèque</option>
            </select>
          </div>

          {/* Bouton Réinitialiser */}
          <div>
            <button
              type="button"
              className="btn btn-secondary btn-block"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
                setSelectedStatus('all');
                setSelectedMethod('all');
                setPeriodPreset('this_month');
              }}
              style={{ fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <FiRefreshCw /> Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* ── TABLEAU DES DÉPENSES (RESPONSIVE) ─────────────────────────── */}
      <div
        style={{
          background: '#ffffff',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
          <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569', fontWeight: 700 }}>
                <th style={{ padding: '12px 16px', width: 110 }}>Date</th>
                <th style={{ padding: '12px 16px' }}>Libellé / Objet</th>
                <th style={{ padding: '12px 16px', width: 150 }}>Catégorie</th>
                <th style={{ padding: '12px 16px', width: 140 }}>Bénéficiaire</th>
                <th style={{ padding: '12px 16px', width: 130 }}>Règlement</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', width: 130 }}>Montant</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', width: 110 }}>Statut</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', width: 80 }}>Reçu</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <FiRefreshCw className="spin" /> Chargement des dépenses...
                    </div>
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '50px 0', color: '#64748b' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#334155' }}>Aucune dépense trouvée</div>
                    <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
                      Modifiez vos filtres ou enregistrez une nouvelle dépense en cliquant sur le bouton ci-dessus.
                    </div>
                  </td>
                </tr>
              ) : (
                expenses.map((item) => {
                  const statusInfo = STATUS_CONFIG[item.payment_status] || { label: item.payment_status, color: '#64748b', bg: '#f1f5f9' };
                  const methodInfo = METHOD_CONFIG[item.payment_method] || { label: item.payment_method, icon: '🔹' };

                  return (
                    <tr
                      key={item.id}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      {/* Date */}
                      <td style={{ padding: '12px 16px', color: '#475569', fontWeight: 600 }}>
                        {formatDate(item.expense_date)}
                        {item.receipt_reference && (
                          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                            {item.receipt_reference}
                          </div>
                        )}
                      </td>

                      {/* Libellé & Description */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {item.title}
                          {item.is_recurring && (
                            <span
                              style={{
                                fontSize: 10,
                                background: '#faf5ff',
                                color: '#7c3aed',
                                border: '1px solid #e9d5ff',
                                padding: '1px 6px',
                                borderRadius: 4,
                                fontWeight: 700,
                              }}
                              title={`Dépense récurrente (${item.recurring_frequency || 'mensuelle'})`}
                            >
                              🔄 Récurrent
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.description}
                          </div>
                        )}
                      </td>

                      {/* Catégorie */}
                      <td style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '3px 10px',
                            borderRadius: 16,
                            fontSize: 12,
                            fontWeight: 600,
                            background: '#f1f5f9',
                            color: '#334155',
                            border: `1px solid ${item.category_color || '#cbd5e1'}40`,
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: item.category_color || '#2563eb',
                            }}
                          />
                          {item.category_name || item.category_code}
                        </span>
                      </td>

                      {/* Bénéficiaire */}
                      <td style={{ padding: '12px 16px', color: '#334155' }}>
                        <div style={{ fontWeight: 600 }}>{item.beneficiary || '-'}</div>
                        {item.beneficiary_phone && (
                          <div style={{ fontSize: 11, color: '#64748b' }}>{item.beneficiary_phone}</div>
                        )}
                      </td>

                      {/* Mode de règlement */}
                      <td style={{ padding: '12px 16px', color: '#475569', fontSize: 12 }}>
                        <span>{methodInfo.icon} {methodInfo.label}</span>
                      </td>

                      {/* Montant */}
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, fontSize: 14, color: '#0f172a' }}>
                        {formatMoney(item.amount)}
                      </td>

                      {/* Statut */}
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 700,
                            background: statusInfo.bg,
                            color: statusInfo.color,
                          }}
                        >
                          {statusInfo.label}
                        </span>
                      </td>

                      {/* Justificatif */}
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {item.receipt_url ? (
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => setReceiptModal(item)}
                            title="Visualiser le justificatif / reçu"
                            style={{ color: '#2563eb', background: '#eff6ff', borderRadius: 6, padding: 6 }}
                          >
                            <FiEye size={16} />
                          </button>
                        ) : (
                          <span style={{ color: '#cbd5e1', fontSize: 16 }}>—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                          {item.payment_status === 'pending' && (
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={() => handleQuickMarkPaid(item)}
                              title="Marquer comme payé"
                              style={{ color: '#16a34a', background: '#f0fdf4', borderRadius: 6, padding: 6 }}
                            >
                              <FiCheck size={15} />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => {
                              setEditingExpense(item);
                              setFormModalOpen(true);
                            }}
                            title="Modifier"
                            style={{ color: '#475569', borderRadius: 6, padding: 6 }}
                          >
                            <FiEdit2 size={15} />
                          </button>
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => handleDelete(item.id, item.title)}
                            title="Supprimer"
                            style={{ color: '#dc2626', borderRadius: 6, padding: 6 }}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── PAGINATION ─────────────────────────────────────────────── */}
        {pagination.pages > 1 && (
          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#f8fafc',
              fontSize: 13,
            }}
          >
            <div style={{ color: '#64748b' }}>
              Affichage de {expenses.length} sur {pagination.total} dépenses
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={pagination.offset === 0}
                onClick={() => fetchExpenses(Math.max(0, pagination.offset - pagination.limit))}
              >
                Précédent
              </button>
              <div style={{ padding: '4px 10px', fontWeight: 700, color: '#334155' }}>
                Page {pagination.currentPage} / {pagination.pages}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={pagination.currentPage >= pagination.pages}
                onClick={() => fetchExpenses(pagination.offset + pagination.limit)}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── MODAL FORMULAIRE DÉPENSE (AJOUT / ÉDITION) ────────────────── */}
      <ExpenseFormModal
        isOpen={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingExpense(null);
        }}
        onSubmit={handleSaveExpense}
        initialData={editingExpense}
        categories={categories}
        loading={formSubmitting}
      />

      {/* ── MODAL VISUALISATION JUSTIFICATIF / FACTURE ────────────────── */}
      {receiptModal && (
        <Modal
          isOpen={!!receiptModal}
          onClose={() => setReceiptModal(null)}
          title={`📎 Justificatif : ${receiptModal.title}`}
          maxWidth="700px"
        >
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ marginBottom: 12, fontSize: 13, color: '#64748b' }}>
              Montant : <strong>{formatMoney(receiptModal.amount)}</strong> | Date : {formatDate(receiptModal.expense_date)}
              {receiptModal.receipt_reference && ` | Réf : ${receiptModal.receipt_reference}`}
            </div>

            {receiptModal.receipt_url?.toLowerCase().endsWith('.pdf') ? (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', height: 420 }}>
                <iframe
                  src={receiptModal.receipt_url}
                  title="Aperçu Justificatif PDF"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              </div>
            ) : (
              <div style={{ maxHeight: 450, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, background: '#f8fafc' }}>
                <img
                  src={receiptModal.receipt_url}
                  alt={`Justificatif ${receiptModal.title}`}
                  style={{ maxWidth: '100%', height: 'auto', borderRadius: 6 }}
                />
              </div>
            )}

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 10 }}>
              <a
                href={receiptModal.receipt_url}
                target="_blank"
                rel="noreferrer"
                download
                className="btn btn-primary btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <FiDownload /> Télécharger l'original
              </a>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setReceiptModal(null)}
              >
                Fermer
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL CRÉATION CATÉGORIE PERSONNALISÉE ────────────────────── */}
      {catModalOpen && (
        <Modal
          isOpen={catModalOpen}
          onClose={() => setCatModalOpen(false)}
          title="🏷️ Ajouter une Catégorie de Charge"
          maxWidth="500px"
        >
          <form onSubmit={handleCreateCategory} style={{ fontSize: 13 }}>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
                Nom de la Catégorie *
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Ex: Frais Notaire, Assurance Bureaux, Sécurité..."
                value={newCatData.name}
                onChange={(e) => setNewCatData({ ...newCatData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: 12 }}>
              <label style={{ fontWeight: 700, marginBottom: 4, display: 'block' }}>
                Couleur de badge
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="color"
                  value={newCatData.color}
                  onChange={(e) => setNewCatData({ ...newCatData, color: e.target.value })}
                  style={{ width: 44, height: 36, padding: 2, border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 12, color: '#64748b' }}>Couleur d'identification visuelle</span>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
                Description (optionnelle)
              </label>
              <textarea
                rows={2}
                className="form-control"
                placeholder="Description des types de dépenses regroupées..."
                value={newCatData.description}
                onChange={(e) => setNewCatData({ ...newCatData, description: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCatModalOpen(false)}
                disabled={catSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={catSubmitting}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {catSubmitting ? 'Création...' : 'Créer la catégorie'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default AgencyExpensesPage;
